import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Platform } from "react-native";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Pressable,
  Image,
  Center,
  useToast,
  ScrollView,
  Stack,
  Spinner,
  IconButton,
  ChevronUpIcon,
} from "native-base";
import { LinearGradient } from "expo-linear-gradient";
import { GlobalStyles } from "../../constants/styles";
import { GlobalConstants } from "../../constants/constants";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../../components/Toast";

// const buildApiUrl = (baseUrl, port, endpoint) => {
//   const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
//   return `${cleanBase}:${port}${endpoint}`;
// };

// const COUPON_API_BASE_URL = buildApiUrl(
//   GlobalConstants.servicesBaseUrl,
//   "3004",
//   "/api/coupon"
// );

const COUPON_API_BASE_URL = `${GlobalConstants.COUPONAPIBASEURL}`.replace(/\/$/, "") + "/api/coupon";

const cardWidthProp = ["92%", "86%", "640px"];

let _couponCache = null;

const isCouponExpired = (coupon) => {
  if (!coupon?.expiry_date) return false;
  return new Date(coupon.expiry_date) < new Date();
};


const buildGroups = (rawCoupons) => {
  if (!Array.isArray(rawCoupons) || rawCoupons.length === 0) return [];

  const isTemplate = rawCoupons[0]?.available_count !== undefined ||
    rawCoupons[0]?.total_count !== undefined;

  if (isTemplate) {
    return rawCoupons.map((t) => ({
      templateId: t.id || t.template_id || t.name,
      name: t.name || t.label || "Coupon",
      totalCount: Number(t.total_count ?? t.totalCount ?? 1),
      availableCount: Number(t.available_count ?? t.availableCount ?? 0),
      expiry_date: t.expiry_date,
      representative: t, 
    }));
  }

  const map = {};
  rawCoupons.forEach((coupon) => {
    const key = coupon.name || coupon.label || String(coupon.id);
    if (!map[key]) {
      map[key] = {
        templateId: key,
        name: key,
        totalCount: 0,
        availableCount: 0,
        expiry_date: coupon.expiry_date,
        representative: null,
        _availableQueue: [],
      };
    }
    map[key].totalCount += 1;
    if (!isCouponExpired(coupon)) {
      map[key].availableCount += 1;
      map[key]._availableQueue.push(coupon);
      if (!map[key].representative) map[key].representative = coupon;
    }
  });

  return Object.values(map);
};

const GradientButton = ({
  children,
  onPress,
  width = "auto",
  px = 12,
  py = 12,
  isLoading = false,
  disabled = false,
}) => (
  <Box
    borderRadius={8}
    overflow="hidden"
    borderWidth={1}
    borderColor={disabled ? GlobalStyles.colors.grey || "#ccc" : GlobalStyles.colors.black}
    bg="transparent"
    width={width}
    alignSelf="center"
    opacity={disabled ? 0.45 : 1}
  >
    <LinearGradient
      colors={
        disabled
          ? ["#b0b0b0", "#909090"]
          : [
              GlobalStyles.colors.gradientBlueStart,
              GlobalStyles.colors.gradientBlueEnd,
            ]
      }
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{ width: "100%", alignItems: "center", justifyContent: "center" }}
    >
      <Pressable
        onPress={disabled || isLoading ? undefined : onPress}
        disabled={disabled || isLoading}
        style={{
          width: "100%",
          paddingVertical: py,
          paddingHorizontal: px,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLoading ? (
          <Spinner color={GlobalStyles.colors.mustard} size="sm" />
        ) : (
          <Text
            color={disabled ? "#fff" : GlobalStyles.colors.mustard}
            fontWeight="400"
            fontSize={["13px", "14px", "14px"]}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            {children}
          </Text>
        )}
      </Pressable>
    </LinearGradient>
  </Box>
);

const decodeQRFromFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const img = new window.Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext("2d").drawImage(img, 0, 0);
          const imageData = canvas
            .getContext("2d")
            .getImageData(0, 0, canvas.width, canvas.height);

          if (!window._jsQR) {
            await new Promise((res, rej) => {
              const s = document.createElement("script");
              s.src =
                "https://cdnjs.cloudflare.com/ajax/libs/jsqr/1.4.0/jsQR.min.js";
              s.onload = () => {
                window._jsQR = window.jsQR;
                res();
              };
              s.onerror = () => rej(new Error("Could not load QR decoder."));
              document.head.appendChild(s);
            });
          }

          const result = window._jsQR(
            imageData.data,
            imageData.width,
            imageData.height
          );
          if (result?.data) resolve(result.data);
          else reject(new Error("No QR code found in image."));
        };
        img.onerror = () => reject(new Error("Could not load image."));
        img.src = e.target.result;
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsDataURL(file);
  });

function SaveCouponView({ coupon, onBack }) {
  const qrUri = coupon?.qr_code || coupon?.qrCode;

  const handleOpenLocationToSave = useCallback(async () => {
    if (!qrUri) return;
    if (Platform.OS === "web") {
      try {
        const response = await fetch(qrUri);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `coupon-${coupon?.coupon_code || coupon?.id || "qr"}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        window.open(qrUri, "_blank");
      }
    } else {
      try {
        let MediaLibrary, FileSystem;
        try { MediaLibrary = require("expo-media-library"); } catch (_) { MediaLibrary = null; }
        try { FileSystem = require("expo-file-system"); } catch (_) { FileSystem = null; }

        if (!MediaLibrary || !FileSystem) {
          const { Linking } = require("react-native");
          await Linking.openURL(qrUri);
          return;
        }
        const { status } = await MediaLibrary.requestPermissionsAsync();
        if (status !== "granted") return;
        const fileUri =
          FileSystem.documentDirectory +
          `coupon_${coupon?.coupon_code || coupon?.id || "qr"}.png`;
        await FileSystem.downloadAsync(qrUri, fileUri);
        await MediaLibrary.saveToLibraryAsync(fileUri);
      } catch (e) {
        try {
          const { Linking } = require("react-native");
          await Linking.openURL(qrUri);
        } catch (_) {}
      }
    }
  }, [qrUri, coupon]);

  return (
    <Center py={0}>
      <Box w={cardWidthProp} bg="transparent" borderRadius={12} p={[4, 5, 6]} mt={0}>
        <VStack space={[4, 5, 6]} alignItems="center" w="100%">
          <Text
            fontSize={["16px", "17px", "18px"]}
            fontWeight="500"
            color={GlobalStyles.colors.blue}
            fontFamily={GlobalStyles.text.fontFamily}
            textAlign="center"
          >
            Save Coupon
          </Text>

          <Pressable onPress={handleOpenLocationToSave} w="100%">
            <HStack alignItems="center" space={2} w="100%" justifyContent="center">
              <Text
                fontWeight="600"
                fontSize={["14px", "15px", "16px"]}
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Open location to save coupon
              </Text>
              <Box
                borderWidth={1}
                borderColor={GlobalStyles.colors.black}
                borderRadius={4}
                p={1}
              >
                <Text fontSize="16px">➤</Text>
              </Box>
            </HStack>
          </Pressable>

          <Box
            bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
            borderRadius={8}
            px={8}
            py={8}
            alignItems="center"
            w="100%"
          >
            {qrUri ? (
              <Image
                source={{ uri: qrUri }}
                alt="QR Code"
                style={{ width: 140, height: 140, marginBottom: 24 }}
                resizeMode="contain"
              />
            ) : (
              <Box
                w="140px"
                h="140px"
                mb={6}
                alignItems="center"
                justifyContent="center"
                borderWidth={2}
                borderColor={GlobalStyles.colors.blue}
                borderRadius={4}
              >
                <Text fontSize="48px">⬛</Text>
              </Box>
            )}

            <VStack space={2} alignItems="flex-start" w="100%">
              {[
                ["Coupon Name:", coupon?.name],
                ["Coupon ID:", coupon?.coupon_code || coupon?.id],
                ["Company created:", coupon?.company_name],
                [
                  "Date created:",
                  coupon?.created_at
                    ? new Date(coupon.created_at).toLocaleDateString()
                    : null,
                ],
              ].map(([label, value]) => (
                <HStack key={label} justifyContent="space-between" w="100%">
                  <Text
                    fontWeight="400"
                    fontSize={["13px", "14px", "15px"]}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {label}
                  </Text>
                  <Text
                    color={GlobalStyles.colors.black}
                    fontSize={["13px", "14px", "15px"]}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {value || "—"}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          <Pressable onPress={onBack}>
            <Text
              color={GlobalStyles.colors.blue}
              fontWeight="600"
              fontSize={["14px", "15px", "16px"]}
              fontFamily={GlobalStyles.text.fontFamily}
              textDecorationLine="underline"
            >
              ← Back to Coupons
            </Text>
          </Pressable>
        </VStack>
      </Box>
    </Center>
  );
}

function CouponRedemptionView({ coupon, onBack }) {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);
  const [couponId, setCouponId] = useState(coupon?.coupon_code || "");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [isDecodingQR, setIsDecodingQR] = useState(false);
  const fileInputRef = useRef(null);

  const qrUri = coupon?.qr_code || coupon?.qrCode;

  const handleRedeem = useCallback(async () => {
    const code = couponId.trim();
    if (!code) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a Coupon ID"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    try {
      setIsRedeeming(true);
      await axios.post(
        `${COUPON_API_BASE_URL}/redeem`,
        { coupon_code: code },
        {
          headers: { Authorization: `Bearer ${user?.token}` },
          timeout: 10000,
        }
      );
      setRedeemed(true);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Coupon redeemed successfully!"
          />
        ),
        placement: "top",
        duration: 3000,
      });
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        (error.code === "ECONNABORTED"
          ? "Request timeout. Please try again."
          : "Failed to redeem coupon.");
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsRedeeming(false);
    }
  }, [couponId, user, toast]);

  const handleUploadQR = useCallback(
    async (file) => {
      if (!file) return;
      setIsDecodingQR(true);
      try {
        const decoded = await decodeQRFromFile(file);
        let code = decoded;
        try {
          const parsed = JSON.parse(decoded);
          code = parsed?.coupon_code || parsed?.id || decoded;
        } catch (_) {}
        setCouponId(code);
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message="QR decoded! Coupon ID filled."
            />
          ),
          placement: "top",
          duration: 3000,
        });
      } catch (err) {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.red}
              message={err.message || "Could not decode QR code."}
            />
          ),
          placement: "top",
          duration: 3000,
        });
      } finally {
        setIsDecodingQR(false);
      }
    },
    [toast]
  );

  const triggerUpload = useCallback(() => {
    if (Platform.OS === "web") {
      fileInputRef.current?.click();
    } else {
      (async () => {
        setIsDecodingQR(true);
        try {
          let ImagePicker;
          try {
            ImagePicker = require("expo-image-picker");
          } catch (_) {
            ImagePicker = null;
          }
          if (!ImagePicker) {
            toast.show({
              render: () => (
                <Toast
                  color={GlobalStyles.colors.red}
                  message="Image picker not available."
                />
              ),
              placement: "top",
              duration: 3000,
            });
            return;
          }
          const { status } =
            await ImagePicker.requestMediaLibraryPermissionsAsync();
          if (status !== "granted") return;
          const result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.Images,
            allowsEditing: false,
            quality: 1,
          });
          if (!result.canceled && result.assets?.[0]) {
            toast.show({
              render: () => (
                <Toast
                  color={GlobalStyles.colors.blue || "blue"}
                  message="Please type the Coupon ID shown on your saved coupon."
                />
              ),
              placement: "top",
              duration: 4000,
            });
          }
        } finally {
          setIsDecodingQR(false);
        }
      })();
    }
  }, [toast]);

  return (
    <Center py={0}>
      <Box w={cardWidthProp} bg="transparent" borderRadius={12} p={[4, 5, 6]} mt={0}>
        <VStack space={[4, 5, 6]} alignItems="center" w="100%">
          <Text
            fontSize={["16px", "17px", "18px"]}
            fontWeight="500"
            color={GlobalStyles.colors.blue}
            fontFamily={GlobalStyles.text.fontFamily}
            textAlign="center"
          >
            Coupon Redemption
          </Text>

          {redeemed ? (
            <Box
              bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
              borderRadius={8}
              p={6}
              alignItems="center"
              w="100%"
            >
              <Text
                fontSize={["15px", "16px", "17px"]}
                fontWeight="600"
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
                textAlign="center"
                mb={2}
              >
                ✓ Coupon Redeemed!
              </Text>
              <Text
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
                textAlign="center"
              >
                Your coupon has been successfully redeemed.
              </Text>
            </Box>
          ) : (
            <Box w="100%">
              <VStack space={2} alignItems="center" w="100%" mb={4}>
                <Text
                  fontWeight="500"
                  fontSize={["13px", "14px", "15px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  textAlign="center"
                >
                  Enter Coupon ID
                </Text>
                <Input
                  value={couponId}
                  onChangeText={setCouponId}
                  borderColor={GlobalStyles.colors.black}
                  borderWidth={1.5}
                  borderRadius={6}
                  bg={GlobalStyles.colors.white || "#FFFFFF"}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["sm", "md", "md"]}
                  _focus={{
                    borderColor: GlobalStyles.colors.black,
                    bg: GlobalStyles.colors.white || "#FFFFFF",
                  }}
                  py={2}
                  px={3}
                  w="55%"
                  textAlign="center"
                  placeholder="Enter coupon code"
                  color={GlobalStyles.colors.black}
                  placeholderTextColor="gray.400"
                />
              </VStack>

              <VStack alignItems="center" space={2} mb={4}>
                <Box
                  borderWidth={2}
                  borderColor={GlobalStyles.colors.blue}
                  borderRadius={8}
                  p={4}
                  alignItems="center"
                  justifyContent="center"
                  w="120px"
                  h="120px"
                >
                  {qrUri ? (
                    <Image
                      source={{ uri: qrUri }}
                      alt="Coupon QR Code"
                      style={{ width: 88, height: 88 }}
                      resizeMode="contain"
                    />
                  ) : (
                    <Box
                      w="80px"
                      h="80px"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="56px" lineHeight={64}>
                        ▦
                      </Text>
                    </Box>
                  )}
                </Box>
                <Text
                  fontSize={["12px", "13px", "13px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Scan QR
                </Text>
              </VStack>

              <Box w="100%" alignItems="center" mb={3}>
                {Platform.OS === "web" && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadQR(file);
                      e.target.value = "";
                    }}
                  />
                )}
                <GradientButton
                  onPress={triggerUpload}
                  width="200px"
                  isLoading={isDecodingQR}
                >
                  Upload Saved Coupon
                </GradientButton>
                <Text
                  mt={1}
                  fontSize={["10px", "11px", "11px"]}
                  color="gray.500"
                  fontFamily={GlobalStyles.text.fontFamily}
                  textAlign="center"
                >
                  Upload QR image to auto-fill ID
                </Text>
              </Box>

              <Box w="100%" alignItems="center">
                <GradientButton
                  onPress={handleRedeem}
                  width="150px"
                  isLoading={isRedeeming}
                >
                  Redeem Coupon
                </GradientButton>
              </Box>
            </Box>
          )}

          <Pressable onPress={onBack}>
            <Text
              color={GlobalStyles.colors.blue}
              fontWeight="600"
              fontSize={["14px", "15px", "16px"]}
              fontFamily={GlobalStyles.text.fontFamily}
              textDecorationLine="underline"
            >
              ← Back to Coupons
            </Text>
          </Pressable>
        </VStack>
      </Box>
    </Center>
  );
}

function AvailableCouponsList({ groups, onSave, onRedeem, isLoading, savingId }) {
  return (
    <Center py={0}>
      <Box
        w={cardWidthProp}
        bg="transparent"
        borderRadius={12}
        p={[4, 5, 6]}
        mt={0}
      >
        <VStack space={[4, 5, 5]} alignItems="flex-start" w="100%">
          <Text
            fontSize={["16px", "17px", "18px"]}
            fontWeight="500"
            color={GlobalStyles.colors.blue}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Available Coupons
          </Text>

          {isLoading ? (
            <Center py={8} w="100%">
              <Spinner color={GlobalStyles.colors.blue} size="lg" />
            </Center>
          ) : groups.length === 0 ? (
            <Center py={8} w="100%">
              <Text
                color="gray.500"
                fontFamily={GlobalStyles.text.fontFamily}
                textAlign="center"
              >
                No available coupons at the moment.
              </Text>
            </Center>
          ) : (
            <Box
              bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
              borderRadius={8}
              px={4}
              py={3}
              w="100%"
            >
              <VStack space={4} w="100%">
                {groups.map((group) => {
                  const allGone = group.availableCount <= 0;
                  const isSavingThis = savingId === group.templateId;

                  return (
                    <VStack key={group.templateId} space={1} w="100%">
                      <HStack
                        w="100%"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <VStack flex={1} pr={2} space={0.5}>
                          <Text
                            fontSize={["13px", "14px", "14px"]}
                            color={
                              allGone ? "gray.400" : GlobalStyles.colors.black
                            }
                            fontFamily={GlobalStyles.text.fontFamily}
                            numberOfLines={2}
                          >
                            {group.name}
                          </Text>
                          <Text
                            fontSize={["11px", "11px", "12px"]}
                            fontWeight="500"
                            color={
                              allGone
                                ? GlobalStyles.colors.red || "red"
                                : GlobalStyles.colors.blue || "#1A1A8C"
                            }
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            {allGone
                              ? "All claimed / expired"
                              : `${group.availableCount} of ${group.totalCount} left`}
                          </Text>
                        </VStack>

                        <HStack space={2} alignItems="center">
                          {/* Save button */}
                          {isSavingThis ? (
                            <Box px={3} py={2}>
                              <Spinner
                                color={GlobalStyles.colors.blue}
                                size="sm"
                              />
                            </Box>
                          ) : (
                            <Pressable
                              onPress={
                                allGone
                                  ? undefined
                                  : () => onSave(group)
                              }
                              disabled={allGone}
                            >
                              <Box
                                borderRadius={8}
                                overflow="hidden"
                                borderWidth={1}
                                borderColor={
                                  allGone
                                    ? GlobalStyles.colors.grey || "#ccc"
                                    : GlobalStyles.colors.black
                                }
                                opacity={allGone ? 0.45 : 1}
                              >
                                <LinearGradient
                                  colors={
                                    allGone
                                      ? ["#b0b0b0", "#909090"]
                                      : [
                                          GlobalStyles.colors.gradientBlueStart,
                                          GlobalStyles.colors.gradientBlueEnd,
                                        ]
                                  }
                                  start={{ x: 0, y: 0 }}
                                  end={{ x: 1, y: 0 }}
                                  style={{
                                    paddingHorizontal: 14,
                                    paddingVertical: 8,
                                  }}
                                >
                                  <Text
                                    color={
                                      allGone
                                        ? "#fff"
                                        : GlobalStyles.colors.mustard
                                    }
                                    fontWeight="700"
                                    fontSize={["12px", "13px", "13px"]}
                                    letterSpacing={0.2}
                                    fontFamily={GlobalStyles.text.fontFamily}
                                  >
                                    Save
                                  </Text>
                                </LinearGradient>
                              </Box>
                            </Pressable>
                          )}

                          <Pressable
                            onPress={
                              allGone
                                ? undefined
                                : () => onRedeem(group)
                            }
                            disabled={allGone || isSavingThis}
                          >
                            <Box
                              borderRadius={8}
                              overflow="hidden"
                              borderWidth={1}
                              borderColor={
                                allGone
                                  ? GlobalStyles.colors.grey || "#ccc"
                                  : GlobalStyles.colors.black
                              }
                              opacity={allGone || isSavingThis ? 0.45 : 1}
                            >
                              <LinearGradient
                                colors={
                                  allGone
                                    ? ["#b0b0b0", "#909090"]
                                    : [
                                        GlobalStyles.colors.gradientBlueStart,
                                        GlobalStyles.colors.gradientBlueEnd,
                                      ]
                                }
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 0 }}
                                style={{
                                  paddingHorizontal: 14,
                                  paddingVertical: 8,
                                }}
                              >
                                <Text
                                  color={
                                    allGone
                                      ? "#fff"
                                      : GlobalStyles.colors.mustard
                                  }
                                  fontWeight="700"
                                  fontSize={["12px", "13px", "13px"]}
                                  letterSpacing={0.2}
                                  fontFamily={GlobalStyles.text.fontFamily}
                                >
                                  Redeem
                                </Text>
                              </LinearGradient>
                            </Box>
                          </Pressable>
                        </HStack>
                      </HStack>
                    </VStack>
                  );
                })}
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </Center>
  );
}

export default function CitizenCouponManager({ onBack }) {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);

  const [view, setView] = useState("list");
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const [groups, setGroups] = useState(() => _couponCache?.groups ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const scrollRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const apiErrorShownRef = useRef(false);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${user.token}`,
      };
    }
  }, [user]);

  const fetchCoupons = useCallback(async () => {
    if (!user?.token) return;

    if (_couponCache && _couponCache.groups.length > 0) {
      setGroups(_couponCache.groups);
      return;
    }

    try {
      setIsLoading(true);
      const resp = await axios.get(`${COUPON_API_BASE_URL}?available=true`, {
        headers: { Authorization: `Bearer ${user.token}` },
        timeout: 8000,
      });

      const raw =
        resp.data?.coupons ||
        resp.data?.data ||
        (Array.isArray(resp.data) ? resp.data : []);

      const built = buildGroups(raw);

      _couponCache = { groups: built, fetchedAt: Date.now() };
      setGroups(built);
      apiErrorShownRef.current = false;
    } catch (error) {
      if (!apiErrorShownRef.current) {
        apiErrorShownRef.current = true;
        const msg =
          error.code === "ECONNABORTED"
            ? "Request timeout. Please try again."
            : "Failed to load coupons.";
        toast.show({
          render: () => (
            <Toast color={GlobalStyles.colors.red} message={msg} />
          ),
          placement: "top",
          duration: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.token, toast]);

  useEffect(() => {
    fetchCoupons();
  }, []);


  const decrementGroup = useCallback((templateId) => {
    setGroups((prev) => {
      const updated = prev.map((g) =>
        g.templateId === templateId
          ? {
              ...g,
              availableCount: Math.max(0, g.availableCount - 1),
              _availableQueue: g._availableQueue
                ? g._availableQueue.slice(1)
                : undefined,
              representative:
                g._availableQueue && g._availableQueue.length > 1
                  ? g._availableQueue[1]
                  : g.representative,
            }
          : g
      );
      _couponCache = { groups: updated, fetchedAt: _couponCache?.fetchedAt ?? Date.now() };
      return updated;
    });
  }, []);

  const handleSave = useCallback(
    async (group) => {
      const coupon = group.representative;
      if (!coupon) return;

      try {
        setSavingId(group.templateId);

        const resp = await axios.post(
          `${COUPON_API_BASE_URL}/${coupon.id}/claim`,
          {},
          {
            headers: { Authorization: `Bearer ${user?.token}` },
            timeout: 10000,
          }
        );

        const claimed = resp.data?.coupon || coupon;

        decrementGroup(group.templateId);

        setSelectedCoupon(claimed);
        setView("save");

        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message="Coupon claimed successfully!"
            />
          ),
          placement: "top",
          duration: 3000,
        });
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          (error.code === "ECONNABORTED"
            ? "Request timeout. Please try again."
            : "Failed to claim coupon.");
        toast.show({
          render: () => (
            <Toast color={GlobalStyles.colors.red} message={msg} />
          ),
          placement: "top",
          duration: 3000,
        });
      } finally {
        setSavingId(null);
      }
    },
    [user, toast, decrementGroup]
  );

  const handleRedeem = useCallback(
    async (group) => {
      const coupon = group.representative;
      if (!coupon) return;

      try {
        setSavingId(group.templateId);

        const claimResp = await axios.post(
          `${COUPON_API_BASE_URL}/${coupon.id}/claim`,
          {},
          {
            headers: { Authorization: `Bearer ${user?.token}` },
            timeout: 10000,
          }
        );

        const claimed = claimResp.data?.coupon || coupon;

        decrementGroup(group.templateId);

        setSelectedCoupon(claimed);
        setView("redeem");
      } catch (error) {
        const status = error.response?.status;
        const serverMsg = error.response?.data?.message || "";
        const alreadyClaimed =
          status === 400 &&
          (serverMsg.toLowerCase().includes("not available") ||
            serverMsg.toLowerCase().includes("claimed"));

        if (alreadyClaimed) {
          setSelectedCoupon(coupon);
          setView("redeem");
        } else {
          const msg =
            error.response?.data?.message ||
            (error.code === "ECONNABORTED"
              ? "Request timeout. Please try again."
              : "Failed to claim coupon.");
          toast.show({
            render: () => (
              <Toast color={GlobalStyles.colors.red} message={msg} />
            ),
            placement: "top",
            duration: 3000,
          });
        }
      } finally {
        setSavingId(null);
      }
    },
    [user, toast, decrementGroup]
  );

  const goToTop = () => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  };

  const checkScroll = (event) => {
    setShowScrollBtn(event.nativeEvent.contentOffset.y > 100);
  };

  return (
    <>
      <ScrollView
        ref={scrollRef}
        onScroll={checkScroll}
        scrollEventThrottle={16}
        flex={1}
      >
        <Center>
          <Box safeAreaTop />
          <Stack
            mt={[1, 2, 3]}
            w="100%"
            maxW={["92%", 600, 850]}
            alignItems="center"
            justifyContent="center"
          >
            {onBack && view === "list" && (
              <HStack w="100%" mb={[2, 3, 3]} px={[2, 4, 4]}>
                <Pressable onPress={onBack}>
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontWeight="600"
                    fontSize={["14px", "15px", "16px"]}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    ← Back to Services
                  </Text>
                </Pressable>
              </HStack>
            )}

            {view === "list" && (
              <AvailableCouponsList
                groups={groups}
                onSave={handleSave}
                onRedeem={handleRedeem}
                isLoading={isLoading}
                savingId={savingId}
              />
            )}

            {view === "save" && (
              <SaveCouponView
                coupon={selectedCoupon}
                onBack={() => setView("list")}
              />
            )}

            {view === "redeem" && (
              <CouponRedemptionView
                coupon={selectedCoupon}
                onBack={() => setView("list")}
              />
            )}
          </Stack>
        </Center>
      </ScrollView>

      {showScrollBtn && (
        <IconButton
          position="absolute"
          bottom={10}
          right={10}
          borderRadius="full"
          bg={GlobalStyles.colors.blue}
          _pressed={{ bg: "gray.500" }}
          icon={<ChevronUpIcon color="white" size={6} />}
          onPress={goToTop}
        />
      )}
    </>
  );
}